import { Component, EventListener, GondelBaseComponent } from '@gondel/core';
import { getScreenSize } from '../../../../shared/utils/media-queries/js/screenSizeDetector';

/**
 * header module implementation.
 * @author Srdan Turudic <srdan.turudic@namics.com> and David Damnjanovic <David.damnjanovic@merkle.com>
 */

enum LinkLevelClasses {
	ThirdLevel = 'js-a-link--nav-3-level',
}
enum LinkLevelSelectors {
	First = '.js-a-link--nav-1-level',
	Second = '.js-a-link--nav-2-level',
	Third = '.js-a-link--nav-3-level',
	Fourth = '.js-a-link--nav-4-level',
	Overview = '.m-nav-main__list-item__overview-link .a-link--nav',
	OverviewMobile = '.js-a-link--nav-mobile-overview-level',
	Section = '.m-nav-main__list-item__section-link .a-link--nav',
}
enum Selectors {
	Overlay = '.o-header__overlay',
	Toggle = '.js-header__toggle',
	NavMain = '.o-header__nav',
	ScrollableHelper = '.o-header__nav-wrapper__scrollable-shadow-helper',
	Link = '.a-link--nav',
	LinkMeta = '.a-link--meta',
	LinkBack = '.a-link--nav-back',
	ActiveLinkOnLoad = '.a-link.a-link--nav.state-a-link--active',
	ExpandedLink = '.a-link.a-link--nav.state-a-link--expanded',
	SubNavigationMobile = '.m-nav-main__list-item__children--mobile',
	SubNavigationDesktop = '.m-nav-main__list-item__children--desktop',
	SubNavigationOpen = '.state-m-nav-main__list-item__children--visible',
	MetaNavigationMobile = '.o-header__overlay > .o-header__meta',
	SearchBtn = '.js-m-search__button-open',
}
enum States {
	NavOpened = 'state-o-header--nav-opened',
	NoScroll = 'state-o-header--no-scroll',
	Open = 'state-o-header__overlay--open',
	Close = 'state-o-header__toggle--close',
	LinkActive = 'state-a-link--active',
	LinkExpanded = 'state-a-link--expanded',
	SubNavigationOpen = 'state-m-nav-main__list-item__children--visible',
	ScrollShadowLeft = 'o-header__nav-wrapper__scrollable-shadow-left',
	ScrollShadowRight = 'o-header__nav-wrapper__scrollable-shadow-right',
}

/* eslint-disable @typescript-eslint/no-unused-expressions */
@Component('Header')
export class Header extends GondelBaseComponent {
	private _body: HTMLBodyElement = {} as HTMLBodyElement;
	private _overlay: HTMLDivElement = {} as HTMLDivElement;
	private _toggleBtn: HTMLDivElement = {} as HTMLDivElement;
	private _currentScreenSize: string | null = null;
	private _navMain: HTMLElement = {} as HTMLElement;
	private _scrollableHelper: Element = {} as Element;
	private _subNavigations: NodeListOf<HTMLElement> = {} as NodeListOf<HTMLElement>;
	private _updateNavShadows: any;
	private _updateActiveLinks: any;
	private _firstLevelLinks: NodeListOf<HTMLElement> = {} as NodeListOf<HTMLElement>;;
	private _secondLevelLinks: NodeListOf<HTMLElement> = {} as NodeListOf<HTMLElement>;;
	private _thirdLevelLinks: NodeListOf<HTMLElement> = {} as NodeListOf<HTMLElement>;;
	private _fourthLevelLinks: NodeListOf<HTMLElement> = {} as NodeListOf<HTMLElement>;;
	private _overviewLinks: NodeListOf<HTMLElement> = {} as NodeListOf<HTMLElement>;;
	private _overviewMobileLinks: NodeListOf<HTMLElement> = {} as NodeListOf<HTMLElement>;;
	private _sectionLinks: NodeListOf<HTMLElement> = {} as NodeListOf<HTMLElement>;;
	private _searchBtn: HTMLButtonElement = {} as HTMLButtonElement;

	public start() {
		this._body = document.querySelector('body') as HTMLBodyElement;
		this._overlay = this._ctx.querySelector(Selectors.Overlay) as HTMLDivElement;
		this._toggleBtn = this._ctx.querySelector(Selectors.Toggle) as HTMLDivElement;
		this._currentScreenSize = getScreenSize() as string;
		this._navMain = this._ctx.querySelector(Selectors.NavMain) as HTMLElement;
		this._scrollableHelper = this._ctx.querySelector(Selectors.ScrollableHelper) as Element;
		this._firstLevelLinks = this._ctx.querySelectorAll(LinkLevelSelectors.First);
		this._secondLevelLinks = this._ctx.querySelectorAll(LinkLevelSelectors.Second);
		this._thirdLevelLinks = this._ctx.querySelectorAll(LinkLevelSelectors.Third);
		this._fourthLevelLinks = this._ctx.querySelectorAll(LinkLevelSelectors.Fourth);
		this._overviewLinks = this._ctx.querySelectorAll(LinkLevelSelectors.Overview);
		this._overviewMobileLinks = this._ctx.querySelectorAll(LinkLevelSelectors.OverviewMobile);
		this._sectionLinks = this._ctx.querySelectorAll(LinkLevelSelectors.Section);
		this._searchBtn = this._ctx.querySelector(Selectors.SearchBtn) as HTMLButtonElement;

		this._updateActiveLinks = () => {
			let isFirstLevelActive = false;
			let isSecondLevelActive = false;
			let isThirdLevelActive = false;
			let isFourthLevelActive = false;
			let isOverviewActive = false;
			let isSectionActive = false;

			this._firstLevelLinks.forEach((el) => {
				if (el.classList.contains(States.LinkActive)) {
					isFirstLevelActive = true;
				}
			});
			this._secondLevelLinks.forEach((el) => {
				if (el.classList.contains(States.LinkActive)) {
					isSecondLevelActive = true;
				}
			});
			this._thirdLevelLinks.forEach((el) => {
				if (el.classList.contains(States.LinkActive)) {
					isThirdLevelActive = true;
				}
			});
			this._fourthLevelLinks.forEach((el) => {
				if (el.classList.contains(States.LinkActive)) {
					isFourthLevelActive = true;
				}
			});
			this._overviewLinks.forEach((el) => {
				if (el.classList.contains(States.LinkActive)) {
					isOverviewActive = true;
				}
			});
			this._sectionLinks.forEach((el) => {
				if (el.classList.contains(States.LinkActive)) {
					isSectionActive = true;
				}
			});

			if (this._currentScreenSize !== 'desktop') {
				if (!isThirdLevelActive && isSectionActive) {
					this._overviewLinks.forEach((el) => el.classList.remove(States.LinkActive));
				}
				if (isSecondLevelActive) {
					const currentActiveParent = this._ctx.querySelector(
						`${Selectors.Overlay} ${LinkLevelSelectors.First}${Selectors.ActiveLinkOnLoad}`
					)?.parentNode;
					const currentActiveOverview = currentActiveParent?.querySelector(LinkLevelSelectors.OverviewMobile);
					currentActiveOverview?.classList.remove(States.LinkActive);
				}
				if (isThirdLevelActive) {
					const currentActiveParent = this._ctx.querySelector(
						`${LinkLevelSelectors.Second}${Selectors.ActiveLinkOnLoad}`
					)?.parentNode;
					const currentActiveOverview = currentActiveParent?.querySelector(LinkLevelSelectors.OverviewMobile);
					currentActiveOverview?.classList.remove(States.LinkActive);
				}
			} else {
				if (isThirdLevelActive || isFourthLevelActive) {
					this._overviewLinks.forEach((el) => el.classList.remove(States.LinkActive));
					this._sectionLinks.forEach((el) => el.classList.remove(States.LinkActive));
				}
				if (!isThirdLevelActive && isSectionActive) {
					this._overviewLinks.forEach((el) => el.classList.remove(States.LinkActive));
				}
			}

			// removing active state from 3rd level links when 4th level is active
			if (isFourthLevelActive) {
				this._thirdLevelLinks.forEach((el) => el.classList.remove(States.LinkActive));
				this._overviewMobileLinks.forEach((el) => el.classList.remove(States.LinkActive));
			}
		};

		this._subNavigations =
			this._currentScreenSize !== 'desktop'
				? this._ctx.querySelectorAll(Selectors.SubNavigationMobile)
				: this._ctx.querySelectorAll(Selectors.SubNavigationDesktop);

		this._updateNavShadows = () => {
			const scrollLeft = this._navMain?.scrollLeft;
			const maxScrollLeft = this._navMain?.scrollWidth - this._navMain?.clientWidth;
			// Removing all horizontal shadows if there is no scrollable area.
			if (this._navMain?.scrollWidth <= this._navMain?.clientWidth) {
				this._scrollableHelper?.classList.remove(States.ScrollShadowRight, States.ScrollShadowLeft);
			} else {
				this._scrollableHelper?.classList.toggle(
					States.ScrollShadowRight,
					Math.ceil(scrollLeft) < maxScrollLeft
				);
				this._scrollableHelper?.classList.toggle(States.ScrollShadowLeft, scrollLeft > 0);
			}
		};

		window.addEventListener('resize', () => {
			this._currentScreenSize = getScreenSize();
			this._updateNavShadows();
			this._subNavigations =
				this._currentScreenSize !== 'desktop'
					? this._ctx.querySelectorAll(Selectors.SubNavigationMobile)
					: this._ctx.querySelectorAll(Selectors.SubNavigationDesktop);
			this._currentScreenSize === 'desktop' && this._body.classList.remove(States.NoScroll);
			if (this._currentScreenSize !== 'desktop' && this._overlay.classList.contains(States.Open)) {
				this._toggleBtn.classList.toggle(States.Close);
				this._overlay.classList.remove(States.Open);
				this._ctx.classList.remove(States.NavOpened);
				this._resetMobileNavigation();
			}
			this._updateActiveLinks();
		});

		this._navMain.addEventListener('scroll', () => {
			this._updateNavShadows();
		});

		window.addEventListener('click', ({ target }: MouseEvent) => {
			const targetNode: Node = target as Node;
			if (targetNode && !this._ctx.contains(targetNode)) {
				this._ctx
					.getElementsByClassName(States.SubNavigationOpen)
					.item(0)
					?.classList.remove(States.SubNavigationOpen);
				this._ctx.getElementsByClassName(States.LinkExpanded).item(0)?.classList.remove(States.LinkExpanded);
				this._ctx.classList.remove(States.NavOpened);
			}
		});

		this._updateNavShadows();
		this._updateActiveLinks();
	}

	@EventListener('keyup', document)
	public _onKeyup(event: KeyboardEvent): void {
		if (event.key === 'Escape' && this._overlay.classList.contains(States.Open)) {
			this._toggle();
		}
	}

	private _resetMobileNavigation() {
		if (!this._overlay.classList.contains(States.Open)) {
			this._ctx.querySelectorAll(Selectors.SubNavigationMobile).forEach((el) => {
				el.scrollTo(0, 0);
				el.classList.remove(States.SubNavigationOpen);
			});
			this._body.classList.remove(States.NoScroll);
		}
		this._overlay.querySelector(Selectors.NavMain)?.scrollTo(0, 0);
	}

	/**
	 * Toggle overlay in header
	 */
	@EventListener('click', Selectors.Toggle)
	public _toggle() {
		// disable scrolling if overlay is open
		this._body.classList.toggle(States.NoScroll);
		this._toggleBtn.classList.toggle(States.Close);
		this._overlay.classList.toggle(States.Open);
		this._resetMobileNavigation();
		this._ctx.classList.toggle(States.NavOpened);
	}

	@EventListener('click', Selectors.LinkMeta)
	public _handleMetaClick(event) {
		this._handleClick(event);
	}

	@EventListener('click', Selectors.Link)
	private _handleClick(event) {
		const target = event.currentTarget as HTMLAnchorElement;
		const children =
			this._currentScreenSize !== 'desktop'
				? (target.parentNode?.querySelector(Selectors.SubNavigationMobile) as HTMLElement)
				: (target.parentNode?.querySelector(`:scope > ${Selectors.SubNavigationDesktop}`) as HTMLElement);
		 
		if (children) {
			event.preventDefault();
			event.stopImmediatePropagation();
			if (this._currentScreenSize !== 'desktop') {
				children.classList.add(States.SubNavigationOpen);
			} else {
				children
					.querySelectorAll(Selectors.ExpandedLink)
					.forEach((el) => el.classList.remove(States.LinkExpanded));
				if (children.classList.contains(States.SubNavigationOpen)) {
					children.classList.remove(States.SubNavigationOpen);
					target.classList.remove(States.LinkExpanded);
				} else {
					this._ctx
						.querySelectorAll(Selectors.ExpandedLink)
						.forEach((el) => el.classList.remove(States.LinkExpanded));
					target.classList.add(States.LinkExpanded);
					this._subNavigations.forEach((elem) => {
						elem.classList.remove(States.SubNavigationOpen);
					});
					children.classList.add(States.SubNavigationOpen);
				}
				if (target.classList.contains(LinkLevelClasses.ThirdLevel)) {
					window.open(event.currentTarget.href, target.getAttribute('target') ?? '_self');
				}
			}
		} else {
			this._ctx
				.querySelectorAll(Selectors.ExpandedLink)
				.forEach((el) => el.classList.remove(States.LinkExpanded));
			window.open(event.currentTarget.href, target.getAttribute('target') ?? '_self');
			this._subNavigations.forEach((elem) => {
				elem.classList.remove(States.SubNavigationOpen);
			});
			this._body.classList.remove(States.NoScroll);
			if (this._currentScreenSize !== 'desktop') {
				this._toggle();
			}
		}
	}
	@EventListener('click', Selectors.LinkBack)
	public _handleClickBack(event: Event): void {
		const target = event.currentTarget as HTMLAnchorElement;
		const parent = target.parentNode as HTMLElement;
		if (parent) {
			event.preventDefault();
			event.stopImmediatePropagation();
			if (parent.classList.contains(States.SubNavigationOpen)) {
				parent.classList.remove(States.SubNavigationOpen);
			}
		}
	}

	@EventListener('click', Selectors.SearchBtn)
	public _handleMainNav(event: Event): void {
		this._ctx.getElementsByClassName(States.SubNavigationOpen).item(0)?.classList.remove(States.SubNavigationOpen);
		this._ctx.getElementsByClassName(States.LinkExpanded).item(0)?.classList.remove(States.LinkExpanded);
		this._ctx.classList.remove(States.NavOpened);
	}
}
